import type { ZudokuConfig } from "zudoku";
import { Card, CardGroup } from "./src/components/Card.js";
import { CheckerAddresses } from "./src/components/CheckerAddresses.js";
import { NotFoundPage } from "./src/components/NotFoundPage.js";
import { jsonLdPlugin } from "./src/json-ld-plugin.js";
import { legacyRedirects } from "./src/legacy-redirects.js";
import { docsGroups } from "./src/navigation.js";
import { spectateCss } from "./src/theme.js";

/**
 * Public documentation at https://docs.spectate.net (README.md). Pages are
 * MDX in `pages/`; the API reference is generated from the committed public
 * API spec (`api-reference/v1.json`, a copy of `laravel/openapi/v1.json`).
 */

const pageMeta =
  typeof window === "undefined"
    ? (await import("./src/page-meta.js")).loadPageMeta()
    : {};

const config: ZudokuConfig = {
  canonicalUrlOrigin: "https://docs.spectate.net",
  sitemap: { siteUrl: "https://docs.spectate.net" },
  site: {
    title: "Spectate Docs",
    logo: {
      src: { light: "/logo/light.svg", dark: "/logo/dark.svg" },
      alt: "Spectate",
      width: "116px",
    },
    showPoweredBy: false,
    notFoundPage: <NotFoundPage />,
    footer: {
      position: "center",
      copyright: `© ${new Date().getFullYear()} Spectate. Monitoring, alerting and status pages, made and hosted in the EU.`,
      columns: [
        {
          title: "Product",
          links: [
            { label: "spectate.net", href: "https://spectate.net" },
            { label: "Pricing", href: "https://spectate.net/pricing" },
            { label: "Changelog", href: "https://spectate.net/changelog" },
          ],
        },
        {
          title: "Developers",
          links: [
            { label: "API reference", href: "/api-reference" },
            { label: "MCP server", href: "/developers/mcp" },
            { label: "llms.txt", href: "/llms.txt" },
          ],
        },
        {
          title: "Help",
          links: [
            { label: "Service status", href: "https://status.spectate.net" },
            { label: "Support", href: "mailto:help@spectate.net" },
            { label: "Security", href: "https://spectate.net/security" },
          ],
        },
      ],
    },
  },
  header: {
    navigation: [
      { label: "Status", to: "https://status.spectate.net" },
      { label: "Support", to: "mailto:help@spectate.net" },
      { label: "Dashboard", to: "https://app.spectate.net" },
    ],
  },
  metadata: {
    title: "%s | Spectate Docs",
    defaultTitle: "Spectate Docs",
    description:
      "Set up uptime, server and domain monitoring, alerting, on-call, incidents and status pages in Spectate. Guides, API reference and MCP server.",
    favicon: "/favicon.svg",
    publisher: "Spectate",
    applicationName: "Spectate",
    keywords: [
      "uptime monitoring",
      "server monitoring",
      "status page",
      "incident management",
      "on-call",
      "alerting",
      "EU",
      "Spectate",
    ],
  },
  theme: {
    light: {
      background: "#ffffff",
      foreground: "#0a0a0a",
      card: "#ffffff",
      cardForeground: "#0a0a0a",
      popover: "#ffffff",
      popoverForeground: "#0a0a0a",
      // Yellow on white is ~1.2:1: in light mode the accent is ink, as in the dashboard
      primary: "#0a0a0a",
      primaryForeground: "#ffffff",
      secondary: "#f2f2f2",
      secondaryForeground: "#0a0a0a",
      muted: "#f2f2f2",
      mutedForeground: "#5c5c5c",
      accent: "#efefef",
      accentForeground: "#0a0a0a",
      destructive: "#c92323",
      destructiveForeground: "#ffffff",
      border: "#e6e6e6",
      input: "#d4d4d4",
      ring: "#0a0a0a",
      radius: "0px",
    },
    dark: {
      background: "#0a0a0a",
      foreground: "#ededed",
      card: "#0f0f0f",
      cardForeground: "#ededed",
      popover: "#121212",
      popoverForeground: "#ededed",
      primary: "#ffea00",
      primaryForeground: "#0a0a0a",
      secondary: "#161616",
      secondaryForeground: "#ededed",
      muted: "#161616",
      mutedForeground: "#a1a1a1",
      accent: "#1a1a1a",
      accentForeground: "#ededed",
      destructive: "#f2555a",
      destructiveForeground: "#0a0a0a",
      border: "#222222",
      input: "#2e2e2e",
      ring: "#ffea00",
      radius: "0px",
    },
    customCss: spectateCss,
  },
  syntaxHighlighting: {
    languages: ["bash", "shell", "json", "yaml", "php", "python", "javascript", "typescript", "go", "http", "toml", "dotenv"],
  },
  navigation: [
    {
      type: "category",
      label: "Docs",
      icon: "book-open",
      items: [
        ...(docsGroups as Extract<NonNullable<ZudokuConfig["navigation"]>[number], { type: "category" }>[]),
      ],
    },
    {
      type: "link",
      label: "API reference",
      icon: "braces",
      to: "/api-reference",
    },
    {
      type: "link",
      label: "MCP server",
      icon: "sparkles",
      to: "/developers/mcp",
    },
  ],
  apis: [
    {
      type: "file",
      input: "./api-reference/v1.json",
      path: "/api-reference",
      options: {
        // /v1 has no CORS (docs/public-api.md), so a browser playground can't call it
        disablePlayground: true,
        schemaDownload: { enabled: true, fileName: "spectate-openapi-v1" },
        supportedLanguages: [
          { value: "shell", label: "cURL" },
          { value: "javascript", label: "JavaScript" },
          { value: "python", label: "Python" },
          { value: "php", label: "PHP" },
          { value: "go", label: "Go" },
        ],
      },
    },
  ],
  docs: {
    files: "pages/**/*.{md,mdx}",
    publishMarkdown: true,
    defaultOptions: {
      copyPage: true,
      showLastModified: false,
    },
    llms: {
      llmsTxt: true,
      llmsTxtFull: true,
      title: "Spectate",
      description:
        "Spectate is an EU-hosted platform for uptime, server and domain monitoring, alerting, on-call, incident management and public status pages.",
      instructions:
        "Every page is also available as Markdown: add .md to its URL. The public REST API is at https://api.spectate.net/v1 (team API tokens, OpenAPI at https://docs.spectate.net/api-reference). AI assistants can act in Spectate through the MCP server at https://api.spectate.net/mcp (OAuth).",
    },
  },
  mdx: {
    components: { Card, CardGroup, CheckerAddresses },
  },
  plugins: [jsonLdPlugin(pageMeta)],
  search: {
    type: "pagefind",
  },
  redirects: [{ from: "/", to: "/introduction" }, ...legacyRedirects],
};

export default config;
